// useJanetDesktopNotifications — a desktop notification for EACH new item
// Janet adds (a new message in Today, a conversation she started) — never
// collapsed into one summary notification, however many landed between
// polls (src/lib/janetNotifyQueue.js). Mounted once in the app shell. Polls
// once a minute; the first look only remembers, so opening the app never
// replays old items. Says only the conversation's name ("Today — Tue Oct
// 21"), never its contents.
//
// postHeadsUp (api/_lib/janet-today.js) deliberately restricts its own push
// to the iOS channel — this poller is the web/desktop delivery for exactly
// the same heads-up, so sending both would duplicate the notification on
// any machine with both a desktop/browser session and a registered web push
// subscription.
//
// In the Mac app it follows Settings → Notifications (desktopNotify.js); in a
// browser only when notification permission was already granted.
//
// Two reliability properties, both load-bearing:
//
// 1. The fetch below asks for every unread Janet-started session, not just
//    the newest few. A session stays `unread=true` until it's opened in the
//    UI, so once more than a small page's worth are unread at once, an
//    ORDER BY last_message_at DESC + a small LIMIT would permanently starve
//    whichever ones don't happen to rank in that window every single poll —
//    a session that fell out of the window never gets looked at again, so a
//    brand-new message landing in it would never be noticed. The bound here
//    (FETCH_LIMIT) exists only to cap a pathological runaway query, not to
//    ration which conversations get checked; the DISPLAY rate (how many
//    notifications one look actually shows) is bounded separately, by
//    MAX_NOTIFY_PER_LOOK inside drainJanetActivity, and is unaffected by it.
// 2. `look()` runs through a reentrancy guard (createPollGuard): if the
//    previous look's own round-trip is still in flight when the interval
//    fires again (a slow network call outlasting POLL_MS), the new tick is
//    skipped outright rather than starting a second, overlapping look —
//    two concurrent reads of the same cursor would otherwise independently
//    decide the same message is "new" and could each show it.

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useJanetPage } from '../contexts/JanetPageContext'
import { inDesktopShell, desktopNotifyOn, showDesktopNotification } from '../lib/desktopNotify'
import { runDesktopNotifyLook, createPollGuard, POLL_SKIPPED } from '../lib/janetNotifyQueue'

const POLL_MS = 60_000

// Generous enough that it is never the limiting factor in real use (a
// session is marked read, out of this query entirely, the moment someone
// opens it) — see the file header's point 1. Not "unlimited" only so one
// pathological backlog can't make this a runaway query.
const FETCH_LIMIT = 500

function allowed() {
  try {
    if (inDesktopShell()) return desktopNotifyOn()
    return typeof Notification !== 'undefined' && Notification.permission === 'granted'
  } catch { return false }
}

export function useJanetDesktopNotifications() {
  const { user } = useAuth()
  const userId = user?.id
  const navigate = useNavigate()
  const { pickTopic } = useJanetPage()
  const seen = useRef(null)

  useEffect(() => {
    if (!userId) return undefined
    seen.current = null
    let stopped = false
    const guardedLook = createPollGuard()
    // The guard wraps the WHOLE look, so a tick that starts while the
    // previous one is still awaiting its network round-trip is skipped
    // outright (`outcome === POLL_SKIPPED`) rather than running a second,
    // overlapping look against the same starting cursor.
    const look = async () => {
      const outcome = await guardedLook(() => runDesktopNotifyLook({
        fetchRows: async () => {
          const { data, error } = await supabase.from('janet_chat_sessions')
            .select('id, title, last_message_at, message_count')
            .eq('user_id', userId).eq('started_by', 'janet').eq('unread', true).is('archived_at', null)
            .order('last_message_at', { ascending: false }).limit(FETCH_LIMIT)
          return error ? null : data
        },
        seen: seen.current,
        allowed,
        // One notification per new item — never bundled into a single "N
        // new items" summary. drainJanetActivity (inside
        // runDesktopNotifyLook) commits each item's cursor ONE AT A TIME,
        // and only after it was actually handed to the OS notification API:
        // a thrown exception, or permission/the setting changing mid-drain,
        // leaves that item — and everything still queued behind it — in the
        // queue for the next poll, rather than silently marking it "told"
        // without ever showing it.
        show: (item) => {
          const n = showDesktopNotification('Janet', {
            body: `New in ${item.title}`,
            tag: item.tag,
            onClick: () => { pickTopic(item.id); navigate('/janet/chat') },
          })
          setTimeout(() => n.close(), 10000)
        },
      }))
      if (!stopped && outcome !== POLL_SKIPPED) seen.current = outcome
    }
    look()
    const t = setInterval(look, POLL_MS)
    return () => { stopped = true; clearInterval(t) }
  }, [userId, navigate, pickTopic])
}
